import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Avatar,
  Divider,
  alpha,
  useTheme,
  Stack,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Person as PersonIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import authService from '../../services/authService';
import customerService from '../../services/customerService';
import { useAuth } from '../../hooks/useAuth';

// Styled components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: theme.shape.borderRadius * 1.5,
    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
  }
}));

const LuxuryButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  padding: theme.spacing(1, 2),
  fontWeight: 500,
  boxShadow: '0 3px 6px rgba(0,0,0,0.12)',
  transition: theme.transitions.create(['background-color', 'box-shadow', 'transform']),
  '&:hover': {
    boxShadow: '0 6px 12px rgba(0,0,0,0.2)',
    transform: 'translateY(-2px)'
  },
  '&:active': {
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transform: 'translateY(0)'
  }
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.divider, 0.8)
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderWidth: 1,
    borderColor: theme.palette.primary.main
  },
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  transition: theme.transitions.create(['box-shadow']),
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
  }
}));

const AssignCustomerDialog = ({ 
  open, 
  onClose, 
  customer, 
  onAssignSuccess, 
  isBulkAssign = false,
  customerIds = []
}) => {
  const theme = useTheme();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch users when dialog opens
  useEffect(() => {
    const fetchUsers = async () => {
      if (!open) return;
      
      try {
        setUsersLoading(true);
        const data = await authService.getAllUsers();
        setUsers(data);
        
        // If customer is already assigned, set the selected user
        if (customer?.assignedTo) {
          setSelectedUserId(customer.assignedTo._id || customer.assignedTo);
        } else {
          setSelectedUserId('');
        }
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users');
      } finally {
        setUsersLoading(false);
      }
    };
    
    fetchUsers();
  }, [open, customer]);
  
  const handleSelectChange = (e) => {
    setSelectedUserId(e.target.value);
  };
  
  const handleAssign = async () => {
    if (!selectedUserId) {
      setError('Please select a user');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      if (isBulkAssign) {
        // For bulk assignment
        console.log('Bulk assigning customers:', customerIds, 'to user:', selectedUserId);
        await customerService.bulkAssignCustomers(customerIds, selectedUserId);
      } else {
        // For single customer assignment
        console.log('Assigning customer:', customer._id, 'to user:', selectedUserId);
        await customerService.assignCustomer(customer._id, selectedUserId);
      }
      onAssignSuccess();
      onClose();
    } catch (err) {
      console.error('Error assigning customer(s):', err);
      setError(err.response?.data?.error || 'Failed to assign customer(s)');
    } finally {
      setLoading(false);
    }
  };
  
  // Get appropriate users based on current user's role
  const getFilteredUsers = () => {
    if (currentUser?.role === 'admin') {
      return users;
    } else if (currentUser?.role === 'manager') {
      // Managers can only assign to employees or themselves
      return users.filter(user => 
        user.role === 'employee' || user._id === currentUser.id
      );
    }
    return [];
  };
  
  const filteredUsers = getFilteredUsers();
  
  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.light, 0.05) }}>
        <DialogTitle sx={{ p: 0, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                width: 40,
                height: 40
              }}
            >
              <AssignmentIcon />
            </Avatar>
            <Typography variant="h5" fontWeight={500}>
              {isBulkAssign ? 'Bulk Assign Customers' : 'Assign Customer'}
            </Typography>
          </Box>
        </DialogTitle>
        
        <Divider sx={{ mx: -2 }} />
      </Box>
      
      <DialogContent sx={{ pt: 3, pb: 3 }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3, 
              borderRadius: theme.shape.borderRadius,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}
          >
            {error}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              borderRadius: theme.shape.borderRadius, 
              bgcolor: alpha(theme.palette.secondary.light, 0.1),
              border: `1px solid ${alpha(theme.palette.secondary.light, 0.2)}`,
              width: '100%'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  bgcolor: theme.palette.secondary.light,
                  color: theme.palette.secondary.contrastText,
                  width: 45,
                  height: 45
                }}
              >
                <PersonIcon />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={500}>
                  {isBulkAssign ? 'Multiple Customers' : customer?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isBulkAssign 
                    ? `${customerIds.length} customers selected for assignment` 
                    : customer?.email || 'No email provided'
                  }
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
        
        <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 2 }}>
          Select user to assign to:
        </Typography>
        
        {usersLoading ? (
          <Box display="flex" justifyContent="center" my={3}>
            <CircularProgress />
          </Box>
        ) : (
          <FormControl fullWidth>
            <InputLabel>Assigned To</InputLabel>
            <StyledSelect
              value={selectedUserId}
              onChange={handleSelectChange}
              label="Assigned To"
              disabled={loading}
            >
              <MenuItem value="">
                <em>Not Assigned</em>
              </MenuItem>
              {filteredUsers.map(user => (
                <MenuItem key={user._id} value={user._id} sx={{ py: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar 
                      sx={{ 
                        width: 32, 
                        height: 32,
                        bgcolor: user.role === 'admin' 
                          ? theme.palette.error.main 
                          : user.role === 'manager'
                            ? theme.palette.secondary.main
                            : theme.palette.primary.main
                      }}
                    >
                      {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                    </Avatar>
                    <Stack>
                      <Typography variant="body2" fontWeight={500}>
                        {user.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                        {user.role}
                      </Typography>
                    </Stack>
                  </Stack>
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>
        )}
        
        <Box 
          sx={{ 
            mt: 3, 
            p: 2, 
            borderRadius: theme.shape.borderRadius,
            bgcolor: alpha(theme.palette.info.main, 0.05),
            border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {isBulkAssign 
              ? 'The assigned user will be able to manage all selected customers\' information and communications.'
              : 'The assigned user will be able to manage this customer\'s information and communications.'
            }
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
        <LuxuryButton 
          onClick={onClose} 
          disabled={loading}
          variant="outlined"
          color="secondary"
        >
          Cancel
        </LuxuryButton>
        <LuxuryButton
          onClick={handleAssign}
          variant="contained"
          color="primary"
          disabled={loading || usersLoading}
          sx={{ px: 3 }}
        >
          {loading ? <CircularProgress size={24} /> : `Confirm Assignment`}
        </LuxuryButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default AssignCustomerDialog;